type AuthErrorContext =
  | 'signin'
  | 'signup'
  | 'forgot_password'
  | 'reset_password'
  | 'resend_verification';

type AuthErrorLike = Error & { code?: string; status?: number };

export interface AuthErrorCopy {
  translationKey: string;
  fallback: string;
}

export const AUTH_ROUTES = {
  login: '/login',
  verifyEmail: '/verify-email',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
} as const;

export const AUTH_PUBLIC_ROUTES = [
  AUTH_ROUTES.login,
  AUTH_ROUTES.verifyEmail,
  AUTH_ROUTES.forgotPassword,
  AUTH_ROUTES.resetPassword,
] as const;

export const getAuthRedirectUrl = (path: string): string =>
  `${window.location.origin}${path}`;

export const isRecoveryLinkInUrl = (): boolean => {
  const urlParts = `${window.location.search}&${window.location.hash}`;
  return urlParts.includes('type=recovery');
};

export const mapAuthError = (
  error: AuthErrorLike | null,
  context: AuthErrorContext
): AuthErrorCopy => {
  if (!error) {
    return {
      translationKey: 'auth.errors.unknown.translation',
      fallback: 'Something went wrong. Please try again.',
    };
  }

  const message = error.message?.toLowerCase() ?? '';
  const code = error.code ?? '';

  if (code === 'auth_timeout' || message.includes('timed out')) {
    return {
      translationKey: 'auth.errors.timeout.translation',
      fallback: 'Request timed out. Please try again.',
    };
  }

  if (code === 'over_email_send_rate_limit') {
    return {
      translationKey: 'auth.errors.rate_limited.translation',
      fallback:
        'Too many requests were sent. Please wait a few minutes before trying again.',
    };
  }

  if (
    code === 'email_not_confirmed' ||
    message.includes('email not confirmed')
  ) {
    return {
      translationKey: 'auth.errors.email_not_confirmed.translation',
      fallback:
        'Your account is not verified yet. Please check your email for the verification link.',
    };
  }

  if (
    context === 'signin' &&
    (message.includes('invalid login credentials') ||
      code === 'invalid_credentials')
  ) {
    return {
      translationKey: 'auth.errors.invalid_credentials.translation',
      fallback: 'Invalid email or password.',
    };
  }

  if (
    context === 'reset_password' &&
    (message.includes('expired') ||
      message.includes('invalid token') ||
      message.includes('otp_expired') ||
      code === 'otp_expired')
  ) {
    return {
      translationKey: 'auth.errors.reset_link_invalid.translation',
      fallback:
        'This reset link is invalid or has expired. Please request a new one.',
    };
  }

  if (message.includes('network') || message.includes('failed to fetch')) {
    return {
      translationKey: 'auth.errors.network.translation',
      fallback: 'Network error. Please check your connection and try again.',
    };
  }

  return {
    translationKey: 'auth.errors.unknown.translation',
    fallback: error.message || 'Something went wrong. Please try again.',
  };
};

export const normalizeAuthError = (
  error: unknown,
  fallbackMessage = 'Something went wrong. Please try again.'
): Error => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    return new Error(
      String((error as { message?: string }).message || fallbackMessage)
    );
  }

  return new Error(fallbackMessage);
};

export const createAuthTimeoutError = (
  timeoutMs: number
): Error & { code: string } => {
  const timeoutError = new Error(
    `Request timed out after ${Math.ceil(timeoutMs / 1000)} seconds.`
  ) as Error & { code: string };
  timeoutError.code = 'auth_timeout';
  return timeoutError;
};

export const withTimeout = async <T>(
  operation: Promise<T>,
  timeoutMs: number
): Promise<T> => {
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      operation,
      new Promise<T>((_, reject) => {
        timeoutHandle = setTimeout(() => {
          reject(createAuthTimeoutError(timeoutMs));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }
  }
};
